import type {
  GraphQLDate,
  IGraphQLDateObject,
} from '../graphQL/commons/types'

/**
 * An epoch milliseconds value as an ISO string, or `undefined`.
 *
 * `0` is rejected rather than rendered. The scalar produces it from
 * `new Date(null).getTime()`, so it means "there was no date here" far more
 * often than it means the first instant of 1970 — and no order predates the
 * epoch. Returning `undefined` lets the caller draw nothing, which is the
 * honest rendering; `01 Jan 1970` is a date the shopper would believe.
 */
const fromEpochMs = (value: unknown): string | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return undefined
  }

  const date = new Date(value)

  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

/**
 * A `Date` field from the storefront API as an ISO string.
 *
 * **The API does not send ISO strings.** `dateScalar.ts` serialises every
 * `Date` to `{ timestamp, string }`, so `createdAt` arrives as an object. Hand
 * that object to `new Date()` and you get an `Invalid Date`; hand the result to
 * the library's `formatOrderDate` and it returns `''`, which renders as no date
 * at all. That is COM-823, and it is why this runs at the boundary where the
 * API's shape becomes the app's.
 *
 * Normalising here rather than in the scalar is deliberate: `Date` is used by
 * every date field on every type in the storefront schema, so changing the
 * serialiser is a breaking change for every consumer of that API. Cinnamon
 * absorbs the shape instead.
 *
 * Unrecognised input yields `undefined` rather than a fallback date. A wrong
 * date is worse than a missing one — the caller can render nothing, but it
 * cannot tell that `01 Jan 1970` was a parse failure.
 */
export const toIsoDate = (value?: GraphQLDate): string | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  if (typeof value === 'number') {
    return fromEpochMs(value)
  }

  if (typeof value === 'string') {
    const date = new Date(value)

    return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
  }

  if (typeof value === 'object') {
    // `string` is `toTimeString()` — a time with no date — so it is never a
    // usable fallback when `timestamp` is missing. An object without a usable
    // `timestamp` has no date in it at all.
    return fromEpochMs((value as IGraphQLDateObject).timestamp)
  }

  return undefined
}

export default toIsoDate
